/* =======================================================================
   Knowledge Base — session memory shared by the page and the drawer.
   Azure item 416.

   Everything the KB remembers for the length of a browser session lives in
   sessionStorage under these keys. The page and the follow-along drawer
   both read and write them, which is what lets an article opened in one
   turn up in the other.

     makaniKbSeen          first visit this session shows the landing page
     makaniKbLast          the route to resume — always an article or module
     makaniKbCollapsed     which tree sections the reader has opened
     makaniKbFlagsHidden   "hide open questions", kept across articles
     makaniKbDrawer        the drawer's own state: dismissed / handle hidden /
                           width / query / scroll position

   sessionStorage can throw in strict privacy modes; fall back to memory so
   the page still works — it just forgets the visit on reload.
   ======================================================================= */
export const KB_BASE = '/knowledge-base';

export const SESSION = (() => {
  try { sessionStorage.setItem('__kbt', '1'); sessionStorage.removeItem('__kbt'); return sessionStorage; }
  catch (e) {
    const mem = {};
    return {
      getItem: k => (k in mem ? mem[k] : null),
      setItem: (k, v) => { mem[k] = String(v); },
      removeItem: k => { delete mem[k]; }
    };
  }
})();

export const readJson = (k, fallback) => {
  try { return JSON.parse(SESSION.getItem(k)) || fallback; } catch (e) { return fallback; }
};
export const writeJson = (k, v) => SESSION.setItem(k, JSON.stringify(v));

/* The slug of the article the KB last had open, or null. Derived from the
   resume route rather than stored separately, so it cannot disagree. */
export function lastArticleSlug(){
  const last = SESSION.getItem('makaniKbLast') || '';
  const m = last.match(/\/a\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}
export function setLastArticle(slug){
  SESSION.setItem('makaniKbLast', KB_BASE + '/a/' + encodeURIComponent(slug));
}

/* One event so the drawer can notice the page changing the last article
   (and vice versa) without polling. storage events do not fire in the same
   tab, hence a custom one. */
export const KB_CHANGE = 'makani-kb-change';
export const announce = () => window.dispatchEvent(new Event(KB_CHANGE));

/* ---------- walkthrough hooks into the real app ----------
   The walkthrough (phase 3) advances off state, not clicks. The app exposes
   the two things the walkthrough cannot read from the URL or the DOM:

     makaniKbWoCreated   how many work orders have been created this session;
                         the walkthrough remembers the count when it starts,
                         so "you made one" survives the form being cleared
     makaniKbWalking     whether a walkthrough is running, read by the Create
                         Work Order dialog so its outside-click-to-close is
                         switched off while the tooltip sits beside it */
export const noteWoCreated = (id) => {
  if (id != null) SESSION.setItem('makaniKbWoLastId', String(id));
  SESSION.setItem('makaniKbWoCreated', String(woCreatedCount() + 1));
  announce();
};
export const woCreatedCount = () => Number(SESSION.getItem('makaniKbWoCreated') || 0);
export const woLastCreatedId = () => SESSION.getItem('makaniKbWoLastId') || null;

export const isWalkthroughRunning = () => SESSION.getItem('makaniKbWalking') === '1';
export const setWalkthroughRunning = on => { SESSION.setItem('makaniKbWalking', on ? '1' : '0'); announce(); };

/* "Take me there" in an article asks for a walkthrough by slug; the
   walkthrough component, mounted in the layout, listens. */
export const KB_WALK_START = 'makani-kb-walk-start';
export const requestWalkthrough = slug =>
  window.dispatchEvent(new CustomEvent(KB_WALK_START, { detail: { slug } }));
